<script setup>
/**
 * AppFooter — site-wide footer with grouped links and a disclaimer.
 * -----------------------------------------------------------------
 * Like the header, the footer is context-aware: every link points only at the
 * product currently being viewed. The Google Meet page never links to Zoom and
 * the Zoom page never links to Google Meet, so each page is fully isolated.
 *
 * The disclaimer is important for this educational project: it makes clear the
 * site is a learning reproduction and not affiliated with the brands.
 */
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import GoogleMeetLogo from '@/components/icons/GoogleMeetLogo.vue'
import ZoomLogo from '@/components/icons/ZoomLogo.vue'

const route = useRoute()
const year = new Date().getFullYear()

const isZoom = computed(() => route.path.startsWith('/zoom'))

// Resolve the current product from the route. Resource/download links are then
// built relative to it, so no link ever references the other download page.
const current = computed(() =>
  isZoom.value
    ? { name: 'Zoom', base: '/zoom' }
    : { name: 'Google Meet', base: '/google-meet' }
)

const columns = computed(() => [
  {
    title: 'Download',
    links: [{ label: current.value.name, to: current.value.base }],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Installation guide', to: `${current.value.base}#install` },
      { label: 'System requirements', to: `${current.value.base}#faq` },
      { label: 'FAQs', to: `${current.value.base}#faq` },
    ],
  },
  {
    title: 'About',
    links: [
      { label: 'Privacy', href: '#' },
      { label: 'Terms', href: '#' },
    ],
  },
])
</script>

<template>
  <footer class="footer">
    <div class="container footer__inner">
      <div class="footer__brand">
        <!-- Per-product logo, matching the header. -->
        <ZoomLogo v-if="isZoom" :size="34" with-wordmark />
        <GoogleMeetLogo v-else :size="34" with-wordmark />
        <p class="footer__tagline">
          Download {{ current.name }} for desktop and mobile, with installation
          guidance and answers to common questions.
        </p>
      </div>

      <nav class="footer__cols" aria-label="Footer">
        <div v-for="col in columns" :key="col.title" class="footer__col">
          <h3 class="footer__col-title">{{ col.title }}</h3>
          <ul>
            <li v-for="link in col.links" :key="link.label">
              <RouterLink v-if="link.to" :to="link.to" class="footer__link">
                {{ link.label }}
              </RouterLink>
              <a v-else :href="link.href" class="footer__link">{{ link.label }}</a>
            </li>
          </ul>
        </div>
      </nav>
    </div>
  </footer>
</template>

<style scoped>
.footer {
  margin-top: var(--space-9);
  border-top: 1px solid var(--border);
  background: var(--bg-subtle);
  padding-block: var(--space-8) var(--space-6);
}
.footer__inner {
  display: grid;
  grid-template-columns: 1.5fr 2fr;
  gap: var(--space-7);
}
.footer__tagline {
  color: var(--text-muted);
  max-width: 36ch;
  font-size: 0.9375rem;
  margin-top: var(--space-4);
}
.footer__cols {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-5);
}
.footer__col-title {
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-faint);
  margin-bottom: var(--space-4);
}
.footer__col ul {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.footer__link {
  color: var(--text-muted);
  font-size: 0.9375rem;
  transition: color var(--transition);
}
.footer__link:hover {
  color: var(--text);
}
.footer__bottom {
  margin-top: var(--space-7);
  padding-top: var(--space-5);
  border-top: 1px solid var(--border);
  color: var(--text-faint);
  font-size: 0.8125rem;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.footer__disclaimer {
  max-width: 80ch;
  line-height: 1.5;
}

/* Tablet: stack the brand block above the link columns. */
@media (max-width: 768px) {
  .footer__inner {
    grid-template-columns: 1fr;
    gap: var(--space-6);
  }
  .footer__cols {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Small phones: a single column of links is easier to scan and tap. */
@media (max-width: 420px) {
  .footer__cols {
    grid-template-columns: 1fr;
    gap: var(--space-5);
  }
}
</style>
